"use client";

import { Trans } from "@lingui/react/macro";
import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import { useWorkspaceSidebarData } from "../../../_contexts/workspace-sidebar-data-context";
import AdsSidebarGroup from "./ads-sidebar-group";
import { DashboardSidebarGroup } from "./dashboard-sidebar-group";
import IndexWorkerSidebarGroup from "./index-worker-sidebar-group";
import SaSidebarGroup from "./sa-sidebar-group";
import { LocalizedSidebarSearchProvider } from "./sidebar-search-context";
import { SidebarSearchEmpty, SidebarSearchInput } from "./sidebar-search-input";
import type { CollapsibleState } from "./types";
import UaSidebarGroup from "./ua-sidebar-group";
import { buildWorkspaceSidebarModel } from "./workspace-sidebar-data-model";

type Props = {
  workspaceId: string;
  initialGroupState: CollapsibleState;
};

const SKELETON_GROUPS = [
  { hasRowIcon: false, titleWidth: "w-20" },
  { hasRowIcon: true, titleWidth: "w-24" },
  { hasRowIcon: true, titleWidth: "w-28" },
  { hasRowIcon: true, titleWidth: "w-24" },
  { hasRowIcon: true, titleWidth: "w-24" },
] as const;

const SKELETON_ROW_WIDTHS = ["w-28", "w-32", "w-24"] as const;

function ProjectsSidebarGroupsSkeleton() {
  return (
    <div
      role="status"
      aria-busy="true"
      className="flex min-h-0 flex-1 flex-col"
    >
      <span className="sr-only">
        <Trans>Loading...</Trans>
      </span>
      <div className="shrink-0 pt-2">
        <div className="px-2 pb-2">
          <Skeleton className="h-7 w-full" />
        </div>
      </div>
      <ScrollArea
        className="min-h-0 flex-1 pr-1"
        viewportClassName="[&>div]:!block [&>div]:w-full [&>div]:max-w-full scrollbar-gutter-stable scrollbar-x-hidden overscroll-x-none"
      >
        {SKELETON_GROUPS.map(({ hasRowIcon, titleWidth }, groupIndex) => (
          <SidebarGroup
            key={`${titleWidth}-${groupIndex}`}
            className="group/sidebar-group relative !px-0 !py-2"
          >
            <div className="flex h-7 w-full min-w-0 items-center gap-1 px-2">
              <Skeleton className="size-4 shrink-0 rounded-sm" />
              <Skeleton className={`h-3 ${titleWidth}`} />
              <Skeleton className="ml-auto mr-1.5 size-4 rounded-sm" />
            </div>
            <SidebarGroupContent className="border-l border-sidebar-border pl-5 pr-2">
              <SidebarMenu>
                {SKELETON_ROW_WIDTHS.map((rowWidth, rowIndex) => (
                  <SidebarMenuItem key={`${rowWidth}-${rowIndex}`}>
                    <div className="ml-[-4px] flex h-7 items-center gap-2 px-2">
                      {hasRowIcon ? (
                        <Skeleton className="size-4 shrink-0 rounded-sm" />
                      ) : null}
                      <Skeleton className={`h-3 ${rowWidth}`} />
                    </div>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  );
}

/** provider が保持する最後の成功 snapshot からサイドバーを描画する。 */
export default function ProjectsSidebarGroups({
  workspaceId,
  initialGroupState,
}: Props) {
  const { data, error, isColdLoading, refresh } = useWorkspaceSidebarData();
  const model = useMemo(() => buildWorkspaceSidebarModel(data), [data]);

  if (isColdLoading) return <ProjectsSidebarGroupsSkeleton />;

  if (!data && error) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 px-4 text-center">
        <p className="text-muted-foreground text-sm">
          <Trans>Failed to load sidebar.</Trans>
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => refresh("manual-retry")}
        >
          <Trans>Retry</Trans>
        </Button>
      </div>
    );
  }

  return (
    <LocalizedSidebarSearchProvider
      sources={model.searchSources}
      workspaceId={workspaceId}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="shrink-0 pt-2">
          <SidebarSearchInput />
        </div>
        <ScrollArea
          className="min-h-0 flex-1 pr-1"
          viewportClassName="[&>div]:!block [&>div]:w-full [&>div]:max-w-full scrollbar-gutter-stable scrollbar-x-hidden overscroll-x-none"
        >
          <DashboardSidebarGroup
            workspaceId={workspaceId}
            dashboardData={model.dashboards}
            initialGroupState={initialGroupState}
          />
          <IndexWorkerSidebarGroup
            workspaceId={workspaceId}
            projectsData={model.iw.projects}
            initialGroupState={initialGroupState}
            workspaceUsedColors={model.workspaceUsedColors}
          />
          <SaSidebarGroup
            workspaceId={workspaceId}
            projectsData={model.sa.projects}
            initialGroupState={initialGroupState}
            onboardingSessions={model.sa.unfinished}
            completedOnboardingSessions={model.sa.completed}
            workspaceUsedColors={model.workspaceUsedColors}
          />
          <UaSidebarGroup
            workspaceId={workspaceId}
            projectsData={model.ua.projects}
            initialGroupState={initialGroupState}
            onboardingSessions={model.ua.unfinished}
            completedOnboardingSessions={model.ua.completed}
            workspaceUsedColors={model.workspaceUsedColors}
          />
          {model.ads.enabled ? (
            <AdsSidebarGroup
              workspaceId={workspaceId}
              projectsData={model.ads.projects}
              initialGroupState={initialGroupState}
              workspaceUsedColors={model.workspaceUsedColors}
            />
          ) : null}
          <SidebarSearchEmpty />
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </div>
    </LocalizedSidebarSearchProvider>
  );
}
